/**
 * Pure string formatters for PII redaction. Stateless — the React layer
 * (``<PiiMask>``) handles when to mask; these helpers handle how.
 *
 * Mirrors the backend's defense-in-depth Aadhar/ABHA masking but is
 * frontend-only: backend is still the source of truth for cross-user
 * visibility (see ``UserProfile`` serializer and ``ClinicalNote``
 * ``hide_from_client`` / ``is_sensitive`` filters).
 */

const BULLET = '•';

export const initialsOf = (firstName?: string | null, lastName?: string | null): string => {
  const f = (firstName ?? '').trim();
  const l = (lastName ?? '').trim();
  const fi = f ? `${f[0].toUpperCase()}.` : '';
  const li = l ? `${l[0].toUpperCase()}.` : '';
  return [fi, li].filter(Boolean).join(' ') || `${BULLET}${BULLET}${BULLET}`;
};

export const maskName = (fullName?: string | null): string => {
  if (!fullName) return `${BULLET}${BULLET}${BULLET}`;
  const parts = fullName.trim().split(/\s+/);
  return parts
    .map((p) => (p[0] ? `${p[0].toUpperCase()}.` : ''))
    .filter(Boolean)
    .join(' ');
};

export const maskEmail = (email?: string | null): string => {
  if (!email) return `${BULLET.repeat(3)}@${BULLET.repeat(3)}.${BULLET.repeat(3)}`;
  const [, domain] = email.split('@');
  if (!domain) return BULLET.repeat(email.length);
  return `${BULLET.repeat(3)}@${BULLET.repeat(3)}.${BULLET.repeat(3)}`;
};

export const maskPhone = (phone?: string | null): string => {
  if (!phone) return `${BULLET.repeat(3)} ${BULLET.repeat(3)} ${BULLET.repeat(4)}`;
  return `${BULLET.repeat(3)} ${BULLET.repeat(3)} ${BULLET.repeat(4)}`;
};

export const maskDate = (_iso?: string | null): string =>
  `${BULLET.repeat(2)}/${BULLET.repeat(2)}/${BULLET.repeat(4)}`;

export const maskAddress = (_address?: string | null): string => BULLET.repeat(3);

/**
 * Reveal only the last 4 of a government ID. Matches the always-on
 * server-side masking planned for Aadhar / ABHA in B4 of the plan.
 */
export const maskGovernmentId = (id?: string | null): string => {
  if (!id) return `${BULLET.repeat(4)} ${BULLET.repeat(4)} ${BULLET.repeat(4)}`;
  const last4 = id.slice(-4);
  return `${BULLET.repeat(4)} ${BULLET.repeat(4)} ${last4 || BULLET.repeat(4)}`;
};

export const NOTE_HIDDEN_PLACEHOLDER = '[Hidden — click to reveal]';
